import { BiomeConfig, RespawnThreshold } from '../types/floor.types';
import { DEFAULT_LOCATIONS } from './locations.config';
import {
  VAULT_ROOM,
  CHALLENGE_ROOM,
  LAIR_KITSUNE_ROOM,
  LAIR_JINN_ROOM,
  LAIR_PARASITE_ROOM,
} from './room-templates.config';

// ---------------------------------------------------------------------------
// Biome configurations
// ---------------------------------------------------------------------------
//
// A biome carries the construction + population of a floor (style, size, room
// count, tileset, music, spawn table, location pool). Dungeons reference biomes
// by id through their `biomeBands` (see `dungeons.config.ts`).
// ---------------------------------------------------------------------------

const NO_RESPAWN: ReadonlyArray<RespawnThreshold> = [
  { maxTicks: 60, rate: 0.0 },
  { maxTicks: 120, rate: 0.0 },
  { maxTicks: 200, rate: 0.0 },
  { maxTicks: Infinity, rate: 0.0 },
];

const DEFAULT_ELITE_CHANCE = 0.15;

const RIFT_SPAWN_WEIGHTS: Record<string, number> = {
  'type2-00': 9,
  'type2-01': 9,
  'type2-02': 9,
  'type2-03': 9,
  'type2-04': 9,
  'type2-05': 9,
  'type2-06': 9,
  'type2-07': 9,
  'type2-08': 9,
  'type2-09': 9,
  'type2-10': 9,
};

export const BIOME_REGISTRY: Record<string, BiomeConfig> = {
  'clear-forest': {
    id: 'clear-forest',
    label: 'Forêt claire',
    style: 'open',
    tileset: 'beta-clear-forest',
    width: 40,
    height: 30,
    roomCount: [3, 4],
    spawnWeights: { frog: 25, dragonfly: 25, ducky: 25, beaver: 25 },
    respawnRate: NO_RESPAWN,
    eliteChance: DEFAULT_ELITE_CHANCE,
    musicId: 'spirits-forest',
    transitionImage: '',
    possibleLocations: [...DEFAULT_LOCATIONS],
  },
  'damp-caves': {
    id: 'damp-caves',
    label: 'Grottes humides',
    style: 'cavern',
    tileset: 'beta-damp-caves',
    width: 40,
    height: 30,
    roomCount: [3, 4],
    spawnWeights: { bat: 25, worm: 25, molerat: 25, 'shoebill-chick': 25 },
    respawnRate: NO_RESPAWN,
    eliteChance: DEFAULT_ELITE_CHANCE,
    musicId: 'hidden-cavern',
    transitionImage: '',
    possibleLocations: [...DEFAULT_LOCATIONS],
  },
  'old-ruins': {
    id: 'old-ruins',
    label: 'Ruines anciennes',
    style: 'dungeon',
    tileset: 'beta-old-ruins',
    width: 40,
    height: 30,
    roomCount: [3, 4],
    spawnWeights: { knight: 25, lizard: 25, fiend: 25, spider: 25 },
    respawnRate: NO_RESPAWN,
    eliteChance: DEFAULT_ELITE_CHANCE,
    musicId: 'ancient-library',
    transitionImage: '',
    possibleLocations: [...DEFAULT_LOCATIONS],
    roomTemplates: [VAULT_ROOM, CHALLENGE_ROOM],
  },
  'lost-galleries': {
    id: 'lost-galleries',
    label: 'Galeries oubliées',
    style: 'labyrinth',
    tileset: 'beta-lost-galleries',
    width: 40,
    height: 30,
    roomCount: [3, 4],
    spawnWeights: { bat: 25, watcher: 25, dragonfly: 25, worm: 25 },
    respawnRate: NO_RESPAWN,
    eliteChance: DEFAULT_ELITE_CHANCE,
    musicId: 'dwarven-mine',
    transitionImage: '',
    possibleLocations: [...DEFAULT_LOCATIONS],
    roomTemplates: [LAIR_KITSUNE_ROOM, CHALLENGE_ROOM],
  },
  'thorn-maze': {
    id: 'thorn-maze',
    label: "Dédale d'épines",
    style: 'labyrinth',
    tileset: 'beta-thorn-maze',
    width: 40,
    height: 30,
    roomCount: [3, 4],
    spawnWeights: { whisper: 25, watcher: 25, fiend: 25, lizard: 25 },
    respawnRate: NO_RESPAWN,
    eliteChance: DEFAULT_ELITE_CHANCE,
    musicId: 'malicious-scheme',
    transitionImage: '',
    possibleLocations: [...DEFAULT_LOCATIONS],
    roomTemplates: [VAULT_ROOM],
  },
  'windy-peak': {
    id: 'windy-peak',
    label: 'Pic venteux',
    style: 'open',
    tileset: 'beta-windy-peak',
    width: 40,
    height: 30,
    roomCount: [3, 4],
    spawnWeights: { 'small-dragon': 25, dragonfly: 25, rhino: 25, beaver: 25 },
    respawnRate: NO_RESPAWN,
    eliteChance: DEFAULT_ELITE_CHANCE,
    musicId: 'traveling-the-sky',
    transitionImage: '',
    possibleLocations: [...DEFAULT_LOCATIONS],
    roomTemplates: [LAIR_JINN_ROOM],
  },
  'deep-caverns': {
    id: 'deep-caverns',
    label: 'Cavernes profondes',
    style: 'cavern',
    tileset: 'beta-deep-caverns',
    width: 40,
    height: 30,
    roomCount: [4, 6],
    spawnWeights: { cell: 25, worm: 25, molerat: 25, rhino: 25 },
    respawnRate: NO_RESPAWN,
    eliteChance: DEFAULT_ELITE_CHANCE,
    musicId: 'dangerous-cave',
    transitionImage: '',
    possibleLocations: [...DEFAULT_LOCATIONS],
    roomTemplates: [LAIR_PARASITE_ROOM, VAULT_ROOM],
  },
  'summit-keep': {
    id: 'summit-keep',
    label: 'Citadelle du sommet',
    style: 'dungeon',
    tileset: 'beta-summit-keep',
    width: 40,
    height: 30,
    roomCount: [4, 6],
    spawnWeights: { knight: 25, demon: 25, 'small-dragon': 25, fiend: 25 },
    respawnRate: NO_RESPAWN,
    eliteChance: DEFAULT_ELITE_CHANCE,
    musicId: 'royal-castle',
    transitionImage: '',
    possibleLocations: [...DEFAULT_LOCATIONS],
    roomTemplates: [VAULT_ROOM, CHALLENGE_ROOM],
  },
  'still-ponds': {
    id: 'still-ponds',
    label: 'Étangs dormants',
    style: 'cavern',
    tileset: 'beta-still-ponds',
    width: 40,
    height: 30,
    roomCount: [4, 6],
    spawnWeights: { hydra: 25, frog: 25, beaver: 25, worm: 25 },
    respawnRate: NO_RESPAWN,
    eliteChance: DEFAULT_ELITE_CHANCE,
    musicId: 'unknown-island',
    transitionImage: '',
    possibleLocations: [...DEFAULT_LOCATIONS],
  },
  'calm-glade': {
    id: 'calm-glade',
    label: 'Clairière paisible',
    style: 'open',
    tileset: 'beta-calm-glade',
    width: 40,
    height: 30,
    roomCount: [3, 4],
    spawnWeights: { phoenixling: 25, 'dino-rex': 25, astronaut: 25, ducky: 25 },
    respawnRate: NO_RESPAWN,
    eliteChance: DEFAULT_ELITE_CHANCE,
    musicId: 'peaceful-village',
    transitionImage: '',
    possibleLocations: [...DEFAULT_LOCATIONS],
  },
  'endless-rift': {
    id: 'endless-rift',
    label: 'Faille sans fond',
    style: 'cavern',
    tileset: 'beta-endless-rift',
    width: 40,
    height: 30,
    roomCount: [4, 6],
    spawnWeights: RIFT_SPAWN_WEIGHTS,
    respawnRate: NO_RESPAWN,
    eliteChance: DEFAULT_ELITE_CHANCE,
    musicId: 'the-evil-one',
    transitionImage: '',
    possibleLocations: [...DEFAULT_LOCATIONS],
  },
  'endless-rift-deep': {
    id: 'endless-rift-deep',
    label: 'Faille — tréfonds',
    style: 'cavern',
    tileset: 'beta-endless-rift-deep',
    width: 40,
    height: 30,
    roomCount: [4, 6],
    spawnWeights: RIFT_SPAWN_WEIGHTS,
    respawnRate: NO_RESPAWN,
    eliteChance: DEFAULT_ELITE_CHANCE,
    musicId: 'demon-king-castle',
    transitionImage: '',
    possibleLocations: [...DEFAULT_LOCATIONS],
  },
  test: {
    id: 'test',
    label: 'Arène de test',
    style: 'open',
    generation: 'test-arena',
    tileset: 'beta-clear-forest',
    width: 25,
    height: 25,
    roomCount: [1, 1],
    // No random spawns — the test arena places fixed dummies (populateTestArena).
    spawnWeights: {},
    respawnRate: [{ maxTicks: Infinity, rate: 0.0 }],
    musicId: 'peaceful-village',
    transitionImage: '',
    possibleLocations: [],
  },
};

// ---------------------------------------------------------------------------
// Respawn helpers
// ---------------------------------------------------------------------------

export const RESPAWN_BASE_INTERVAL = 40; // ticks between spawn attempts

export function getRespawnRate(
  ticksOnFloor: number,
  thresholds: ReadonlyArray<RespawnThreshold>,
): number {
  for (const t of thresholds) {
    if (ticksOnFloor <= t.maxTicks) {
      return t.rate;
    }
  }
  return 0;
}
